import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Activity,

  // Bot, // unused while Automation is commented out
  Briefcase,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  FileText,
  LayoutDashboard,
  LogOut,
  Megaphone,
  MessageSquare,
  Settings,
  ShieldCheck,
  ShieldAlert,
  Sparkles,
  User,
  Users,
  History,
  Database,
  BookOpen,
  Globe,
} from "lucide-react";
import { cn } from "../lib/utils";
import logo from "../assets/Divinifyy.png";
import { Button } from "./ui/button";
import { useIsMobile } from "../utils/useIsMobile";
import { getUserRole, getUserDepartment } from "../utils/auth";
import { ROLE_ALLOWED_PATHS } from "../routes/AppRoutes";

interface SidebarProps {
  onLogout?: () => void;
}

/** Returns a human-readable label for each role. */
const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: "Super Admin",
  ADMIN:       "Admin",
  SUPPORT:     "Support",
  FINANCE:     "Finance",
  AD_MANAGER:  "Ad Manager",
  OPERATIONS:  "Operations",
};

const ALL_SECTIONS = [
  {
    title: "Main",
    items: [
      { label: "Overview",  path: "/",         icon: LayoutDashboard },
      { label: "Scholars",  path: "/scholars",  icon: Sparkles, badge: 12 },
      { label: "Users",     path: "/users",     icon: Users },
      { label: "Bookings",  path: "/bookings",  icon: Briefcase },
    ],
  },
  {
    title: "Operations",
    items: [
      { label: "Services",          path: "/services",        icon: ShieldCheck },
      { label: "Finance",           path: "/finance",         icon: CircleDollarSign },
      { label: "Payouts & Refunds", path: "/finance/payouts", icon: CircleDollarSign },
      { label: "Cancellation Policy", path: "/cancellation-policy", icon: ShieldCheck },
      { label: "Push Campaigns",    path: "/push-campaigns",  icon: Megaphone },
      { label: "Advertisements",    path: "/ads",             icon: Sparkles },
      { label: "Platform Configs",   path: "/platform-configs", icon: Database },
      { label: "Content & Docs",     path: "/content-docs",    icon: BookOpen },
      { label: "Religion Data",      path: "/religion-data",   icon: Globe },
    ],
  },
  {
    title: "System",
    items: [
      { label: "Analytics",   path: "/analytics",   icon: Activity },
      // { label: "Automation",  path: "/automation",  icon: Bot },  // temporarily disabled
      { label: "Support",     path: "/support",     icon: User },
      { label: "Messages",    path: "/messages",    icon: MessageSquare },
      { label: "Reports",     path: "/reports",     icon: FileText },
      { label: "Settings",    path: "/settings",    icon: Settings },
      { label: "Admin Logs",  path: "/admin-logs",  icon: History },
      { label: "Admins",      path: "/admins",      icon: ShieldAlert },
    ],
  },
];

const Sidebar = ({ onLogout }: SidebarProps) => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const role = getUserRole();
  const department = getUserDepartment();
  const [collapsed, setCollapsed] = useState(isMobile);

  useEffect(() => {
    if (isMobile) setCollapsed(true);
  }, [isMobile]);

  /** Filter sections and items to only those the current role may access. */
  const sections = useMemo(() => {
    if (role === "SUPER_ADMIN") return ALL_SECTIONS;
    if (role === "ADMIN" && !department) return ALL_SECTIONS;

    const effectiveRole = department || role || "";
    const allowed = ROLE_ALLOWED_PATHS[effectiveRole] || ROLE_ALLOWED_PATHS[role || ""];
    const isAllAccess = allowed?.includes("*");

    return ALL_SECTIONS.map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        if (isAllAccess) return true;
        return allowed?.some(
          (p) => item.path === p || item.path.startsWith(p + "/")
        );
      }),
    })).filter((section) => section.items.length > 0);
  }, [role, department]);

  const brandTitle = useMemo(() => {
    const effectiveRole = (department || role || "").toUpperCase();

    if (effectiveRole.includes("SUPPORT")) return "Divinifyy Support";
    if (effectiveRole.includes("OPERATIONS") || effectiveRole.includes("OPS")) return "Divinifyy Operations";
    if (effectiveRole.includes("FINANCE")) return "Divinifyy Finance";
    if (effectiveRole.includes("MARKETING") || effectiveRole.includes("AD_MANAGER")) return "Divinifyy Marketing";
    if (effectiveRole.includes("MODERAT") || effectiveRole.includes("COMPLIANCE")) return "Divinifyy Moderation";

    return "Divinifyy Admin";
  }, [role, department]);

  useEffect(() => {
    document.title = brandTitle;
  }, [brandTitle]);

  return (
    <aside
      style={{
        width: collapsed ? (isMobile ? 72 : 84) : 264,
        transition: "width 220ms ease",
        borderRight: "1px solid hsl(var(--border))",
        background: "hsl(var(--secondary))",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ padding: 16, borderBottom: "1px solid hsl(var(--border))" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <div
              style={{
                width: 34,
                height: 34,
                display: "grid",
                placeItems: "center",
                overflow: "hidden"
              }}
            >
              <img src={logo} alt="divinifyy" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            </div>
            {!collapsed && <strong style={{ fontFamily: "Manrope, sans-serif", fontSize: 14 }}>{brandTitle}</strong>}
          </div>
          <Button variant="ghost" onClick={() => setCollapsed((prev) => !prev)} aria-label="Toggle sidebar">
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </Button>
        </div>
      </div>

      <div className="soft-scroll" style={{ overflowY: "auto", padding: "10px", flex: 1 }}>
        {sections.map((section) => (
          <div key={section.title} style={{ marginTop: 8 }}>
            {!collapsed && (
              <p
                style={{
                  fontSize: 11,
                  color: "hsl(var(--muted-foreground))",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  margin: "8px 10px",
                }}
              >
                {section.title}
              </p>
            )}
            {section.items.map((item) => {
              const active = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn("sidebar-link", active && "active")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: collapsed ? "center" : "space-between",
                    textDecoration: "none",
                    borderRadius: 12,
                    padding: collapsed ? "11px 8px" : "10px 12px",
                    marginBottom: 6,
                    color: active ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))",
                    border: `1px solid ${active ? "hsl(var(--primary) / 0.5)" : "transparent"}`,
                    background: active ? "hsl(var(--primary) / 0.14)" : "transparent",
                    transition: "all 180ms ease",
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Icon size={16} /> {!collapsed && <span style={{ fontSize: 13 }}>{item.label}</span>}
                  </span>
                  {!collapsed && (item as any).badge ? (
                    <span
                      style={{
                        fontSize: 11,
                        padding: "2px 8px",
                        borderRadius: 999,
                        background: "hsl(var(--muted))",
                        color: "hsl(var(--foreground))",
                      }}
                    >
                      {(item as any).badge}
                    </span>
                  ) : null}
                </Link>
              );
            })}
          </div>
        ))}
      </div>

      <div style={{ borderTop: "1px solid hsl(var(--border))", padding: 12, display: "flex", alignItems: "center", gap: 8 }}>
        {!collapsed && (
          <div style={{ flex: 1, fontSize: 12, color: "hsl(var(--muted-foreground))" }}>
            {ROLE_LABELS[role ?? ""] ?? "Admin"}
          </div>
        )}
        <Button variant="ghost" onClick={onLogout}>
          <LogOut size={15} /> {!collapsed && "Logout"}
        </Button>
      </div>
    </aside>
  );
};

export default Sidebar;
